import * as XLSX from "xlsx";
import { importFileValidationConfig } from "../configuration/importFileValidationConfig";

const DEFAULT_EXTENSIONS = [".xlsx", ".xls", ".csv"];
// Valida solo el tipo de archivo por extension.
export const validateFile = (file, validExtensions = DEFAULT_EXTENSIONS) => {
  if (!file?.name) {
    return false;
  }

  const fileExtension = file.name
    .substring(file.name.lastIndexOf("."))
    .toLowerCase();
  return validExtensions.includes(fileExtension);
};

// Normaliza valores para comparar y validar de forma consistente.
const normalizeString = (value) => String(value ?? "").trim();
const normalizeLower = (value) => normalizeString(value).toLowerCase();
const isEmptyValue = (value) => normalizeString(value) === "";

// Obtiene todas las cabeceras existentes en el fichero (sin duplicados).
const getNormalizedHeaders = (rows) => {
  const headersSet = new Set();

  rows.forEach((row) => {
    Object.keys(row || {}).forEach((rawHeader) => {
      const header = normalizeString(rawHeader);
      if (header) {
        headersSet.add(header);
      }
    });
  });

  return Array.from(headersSet);
};

// Reescribe cada fila usando las mismas cabeceras para facilitar validaciones.
const normalizeRows = (rows, headers) => {
  return rows.map((row) => {
    const normalizedRow = {};

    const normalizedKeys = new Map(
      Object.entries(row || {}).map(([key, value]) => [
        normalizeLower(key),
        value,
      ]),
    );

    headers.forEach((header) => {
      normalizedRow[header] = normalizedKeys.get(normalizeLower(header)) ?? "";
    });

    return normalizedRow;
  });
};

// Busca una cabecera por nombre ignorando mayusculas/minusculas.
const resolveHeader = (headers, expectedHeader) => {
  return headers.find(
    (header) => normalizeLower(header) === normalizeLower(expectedHeader),
  );
};

// Valida contenido real de la hoja: columnas obligatorias y reglas por columna.
export const validateSpreadsheetContent = async (file, t) => {
  const {
    requiredColumns = [],
    columnRules = {},
    requireValueInAllColumns = false,
  } = importFileValidationConfig; // Configuracion global de validacion, se puede extender con reglas personalizadas por caso de uso.

  if (!file) {
    return {
      isValid: false,
      errors: [t("fileValidation.missingFile")],
      headers: [],
      rowCount: 0,
    };
  }

  try {
    // Lee el fichero y toma la primera hoja para validar.
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, {
      type: "array",
      cellStyles: false,
      cellHTML: false,
    });
    const firstSheetName = workbook.SheetNames?.[0];

    if (!firstSheetName) {
      return {
        isValid: false,
        errors: [t("fileValidation.noSheets")],
        headers: [],
        rowCount: 0,
      };
    }
    const sheet = workbook.Sheets[firstSheetName];

    const range = XLSX.utils.decode_range(sheet["!ref"]);

    let lastColumn = -1;

    for (let col = range.s.c; col <= range.e.c; col++) {
      for (let row = range.s.r; row <= range.e.r; row++) {
        const address = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = sheet[address];

        if (cell && cell.v != null && String(cell.v).trim() !== "") {
          lastColumn = col;
          break;
        }
      }
    }

    if (lastColumn >= 0) {
      range.e.c = lastColumn;
    }

    const rawRows = XLSX.utils.sheet_to_json(sheet, {
      defval: "",
      raw: false,
      range,
    });
    const headers = getNormalizedHeaders(rawRows);

    if (headers.length === 0) {
      return {
        isValid: false,
        errors: [t("fileValidation.noHeadersOrRows")],
        headers: [],
        rowCount: 0,
      };
    }

    const rows = normalizeRows(rawRows, headers);
    const errors = [];

    // 1) Valida que existan todas las columnas requeridas.
    requiredColumns.forEach((expectedColumn) => {
      const header = resolveHeader(headers, expectedColumn);
      if (!header) {
        errors.push(
          t("fileValidation.missingColumns", { columns: expectedColumn }),
        );
      }
    });

    // 2) Recorre fila a fila aplicando reglas globales y por columna.
    rows.forEach((row, index) => {
      const rowNumber = index + 2;

      if (requireValueInAllColumns) {
        headers.forEach((header) => {
          if (isEmptyValue(row[header])) {
            errors.push(
              t("fileValidation.requiredValue", { rowNumber, header }),
            );
          }
        });
      }

      Object.entries(columnRules).forEach(([columnName, rule]) => {
        const header = resolveHeader(headers, columnName);
        if (!header) {
          return;
        }

        const value = row[header];
        const valueText = normalizeString(value);

        // Si la columna es obligatoria, no se permite vacio.
        if (rule?.required && isEmptyValue(value)) {
          errors.push(t("fileValidation.requiredValue", { rowNumber, header }));
          return;
        }

        // Si no hay valor y no era required, no se aplican mas reglas.
        if (isEmptyValue(value)) {
          return;
        }

        // Lista blanca de valores permitidos.
        if (
          Array.isArray(rule?.allowedValues) &&
          rule.allowedValues.length > 0
        ) {
          const allowedValues = rule.allowedValues.map((allowed) =>
            normalizeLower(allowed),
          );
          if (!allowedValues.includes(normalizeLower(valueText))) {
            errors.push(
              t("fileValidation.invalidValue", {
                rowNumber,
                header,
                valueText,
              }),
            );
            return;
          }
        }

        // Validacion de formato por expresion regular.
        if (rule?.pattern instanceof RegExp && !rule.pattern.test(valueText)) {
          errors.push(
            t("fileValidation.invalidFormat", { rowNumber, header, valueText }),
          );
          return;
        }
      });
    });

    // Solo es valido cuando no hay ningun error.
    return {
      isValid: errors.length === 0,
      errors,
      headers,
      rowCount: rows.length,
    };
  } catch {
    // Error de lectura/parsing del fichero.
    return {
      isValid: false,
      errors: [
        "No se ha podido leer el archivo. Revisa el formato del Excel/CSV.",
      ],
      headers: [],
      rowCount: 0,
    };
  }
};
